import { useRef, useState, useEffect } from "react";
import { Text, Group, Stack, Paper } from "@mantine/core";
import { IconTrash } from "@tabler/icons-react";
import { Button } from "@onengine/component-library";
import styles from "./Signature.module.scss";

export interface SignatureCanvasProps {
  onConfirm: (url: string) => void;
}

export function SignatureCanvas({ onConfirm }: SignatureCanvasProps) {
  const ref = useRef<HTMLCanvasElement>(null);
  const [drawing, setDrawing] = useState(false);
  const [drawn, setDrawn] = useState(false);
  const pos = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    const r = ref.current!.getBoundingClientRect();
    let clientX: number;
    let clientY: number;
    if ("touches" in e && e.touches.length > 0) {
      clientX = e.touches[0].clientX;
      clientY = e.touches[0].clientY;
    } else {
      clientX = (e as React.MouseEvent<HTMLCanvasElement>).clientX;
      clientY = (e as React.MouseEvent<HTMLCanvasElement>).clientY;
    }
    return { x: clientX - r.left, y: clientY - r.top };
  };
  const start = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    e.preventDefault();
    const c = ref.current!.getContext("2d")!;
    const p = pos(e);
    c.beginPath();
    c.moveTo(p.x, p.y);
    c.strokeStyle = "var(--neutral-1400)";
    c.lineWidth = 2.5;
    c.lineCap = "round";
    c.lineJoin = "round";
    setDrawing(true);
    setDrawn(true);
  };
  const draw = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    if (!drawing) return;
    e.preventDefault();
    const c = ref.current!.getContext("2d")!;
    const p = pos(e);
    c.lineTo(p.x, p.y);
    c.stroke();
  };
  const stop = () => setDrawing(false);
  const clear = () => {
    ref.current!.getContext("2d")!.clearRect(0, 0, ref.current!.width, ref.current!.height);
    setDrawn(false);
  };
  useEffect(() => {
    let cancelled = false;
    const applySize = () => {
      const el = ref.current;
      if (!el || cancelled) return;
      const r = el.getBoundingClientRect();
      if (r.width < 2 || r.height < 2) return false;
      const d = window.devicePixelRatio || 1;
      el.width = Math.floor(r.width * d);
      el.height = Math.floor(r.height * d);
      const ctx = el.getContext("2d");
      if (ctx) {
        ctx.setTransform(1, 0, 0, 1, 0, 0);
        ctx.scale(d, d);
      }
      return true;
    };
    const schedule = () => {
      if (cancelled) return;
      if (!applySize()) requestAnimationFrame(schedule);
    };
    schedule();
    const t1 = window.setTimeout(() => !cancelled && applySize(), 50);
    const t2 = window.setTimeout(() => !cancelled && applySize(), 200);
    return () => {
      cancelled = true;
      window.clearTimeout(t1);
      window.clearTimeout(t2);
    };
  }, []);

  return (
    <Stack gap="sm">
      <Paper radius="lg" className={styles.signatureCanvasPaper}>
        <canvas
          ref={ref}
          className={styles.signatureCanvas}
          onMouseDown={start}
          onMouseMove={draw}
          onMouseUp={stop}
          onMouseLeave={stop}
          onTouchStart={start}
          onTouchMove={draw}
          onTouchEnd={stop}
        />
      </Paper>
      <Text size="sm" className={styles.signatureHint}>
        Use your mouse, trackpad, or finger to sign above
      </Text>
      <Group justify="space-between">
        <Button
          variant="neutral-secondary"
          size="base"
          iconLeft={<IconTrash size={16} />}
          onClick={clear}
        >
          Clear
        </Button>
        <Button
          variant="primary"
          size="base"
          disabled={!drawn}
          onClick={() => onConfirm(ref.current!.toDataURL("image/png"))}
        >
          ✓ Confirm
        </Button>
      </Group>
    </Stack>
  );
}
